import dayjs from 'dayjs'
import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useParams } from 'react-router-dom'
import { Checkbox, Divider, Grid, Icon, Input, Popup, SemanticICONS } from 'semantic-ui-react'
import { MilkMoneyQuery } from '~/QueryHooks/MilkMoneyQuery'
import { DownloadService } from '~/api/services/downloadFiles'
import MilkMoneyAccountingService, { TourDetailData } from '~/api/services/milkMoneyAccounting'
import Layout from '~/components/Layout'
import notify from '~/components/Notify'
import { useDebounce } from 'use-lodash-debounce';
import { StyledGridColumns, StyledGridRow, StyledSegment } from '~/pages/Dairy/MilkFixedPrice/ShowMilkFixedPrice/style'
import { useAuth } from '~/routes/components/AuthProvider'
import { formatNumberWithoutFractions, handleErrors } from '~/utils/helpers'
import TableComponent from '~/components/TableComponent'
import i18n from '~/components/i18n'
import { ColumnLabels, StyledHeader } from './style'
import { El } from '~/components/Layout/style'
import { RouteTypes } from '~/routes/constants'
import AddEditMilkDelivery from './AddEditMilkDelivery'
import { LIGHT_USER_ROLE_SLUG } from '~/Constants'
import ExcelExport from '~/components/ExcelExport'
import { Nullable } from '~/types'
import DeleteTourModal from './Modals/DeleteTourModal'
import ReloadTourModal from './Modals/ReloadTourModal'
import CustomIcon from '~/components/CustomIcon'
import ChangeLogsModal from './ChangeLogsModal'

const getDefaultTableColumnHeaders = () => [
  {
    name: i18n.t('Tour data id'),
    key: 'uniqueId',
    sorted: 'desc'
  },
  {
    name: i18n.t('Time from'),
    key: 'timeFrom',
    sorted: 'desc'
  },
  {
    name: i18n.t('Time to'),
    key: 'timeTo',
    sorted: 'desc'
  },
  {
    name: i18n.t('Supplier No.'),
    key: 'supplierNo',
    type: 'supplier-link',
    sorted: 'desc'
  },
  {
    name: i18n.t('Farmer'),
    key: 'companyName',
    type: 'supplier-link',
    sorted: 'desc'
  },
  {
    name: i18n.t('Amount accepted (in l)'),
    key: 'amount',
    type: 'no-unit-number',
    decimalScale: 1,
    sorted: 'desc'
  },
  {
    name: i18n.t('avg. Temp. (°C)'),
    key: 'avgTemp',
    type: 'no-unit-number',
    decimalScale: 1,
    sorted: 'desc'
  },
  {
    name: i18n.t('Sample bottle'),
    key: 'sampleBottleNumber'
  },
  {
    name: i18n.t('Sample Compartment Temp (°C)'),
    key: 'sampleAvgTemp',
    type: 'no-unit-number',
    decimalScale: 1,
    sorted: 'desc'
  },
  {
    name: i18n.t('Actions')
  }
]

const ShowTours: React.FC = () => {
  const { t } = useTranslation()
  const { id } = useParams()
  const [toursData, setToursData] = useState<Nullable<TourDetailData>>(null)
  const [isBusy, setIsBusy] = useState(false)
  const [search, setSearch] = useState('')
  const debouncedSearchValue = useDebounce(search, 300)
  const [idMilkDelivery, setIdMilkDelivery] = useState(null)
  const { systemData: { logged: { roleSlug } } } = useAuth()
  const lightUserLogged = roleSlug === LIGHT_USER_ROLE_SLUG
  const [dataFiltered, setDataFiltered] = useState({
    page: 0,
    rows: 10,
    search: '',
    column: '',
    sort: '',
    deleted: 0
  })

  const actions = [
    {
      popupMessage: i18n.t('Edit'),
      type: 'icon',
      name: 'pencil',
      size: 'large',
      actionType: 'edit'
    },
    {
      popupMessage: 'Milchanlieferung löschen',
      type: 'icon',
      name: 'trash',
      size: 'large',
      actionType: 'delete',
      verifiedActionKey: 'deleted',
      verifiedAction: 0
    },
    {
      popupMessage: '',
      type: 'icon',
      name: 'comment',
      size: 'large',
      actionType: 'comment',
      renderActions: getActions
    },
    {
      popupMessage: 'Tour wiederherstellen',
      type: 'icon',
      name: 'refresh',
      size: 'large',
      actionType: 'reload',
      verifiedActionKey: 'deleted',
      verifiedAction: 1
    }
  ]

  const [tableColumnHeaders, setTableColumnHeaders] = useState(getDefaultTableColumnHeaders())
  const [reloadTour, setReloadTour] = useState(null)
  const [deleteTour, setDeleteTour] = useState(null)

  const {
    isLoading: isBusyData,
    data: specificTourData,
    refetch: fetchFilteredData,
    isFetching: isFetchingData
  } = MilkMoneyQuery.getMilkMoneySpecificTourData(id, {
    ...dataFiltered,
    page: dataFiltered.page === 0 ? dataFiltered.page : dataFiltered.page - 1
  });

  useEffect(() => {
    setTableColumnHeaders(getDefaultTableColumnHeaders())
  }, [t])

  useEffect(() => {
    fetchData()
  }, [])

  useEffect(() => {
    fetchFilteredData()
  }, [dataFiltered])

  useEffect(() => {
    setDataFiltered({ ...dataFiltered, search: debouncedSearchValue, page: 0 });
  }, [debouncedSearchValue]);

  async function fetchData() {
    setIsBusy(true)
    try {
      const response = await MilkMoneyAccountingService.getMoneyToursData(id)
      setToursData(response.data)
    } catch (error) {
      handleErrors(error)
    }
    setIsBusy(false)
  }

  async function handleDownloadFile(name) {
    try {
      await DownloadService.downloadFile(`milk-money/tours/${id}/get-file`, name)
    } catch (error) {
      handleErrors(error)
    }
  }

  function getActions(tour, actionType) {
    const action = actions.find(action => action.actionType === actionType)
    switch (actionType) {
      case 'comment':
        return tour.comment && tour.deleted === 1 && <Popup
          content={tour?.comment || ''}
          trigger={<CustomIcon
            style={{ cursor: 'pointer' }} name={action.name as SemanticICONS}
            size='large'
          />}
        />
      default:
        return
    }
  }

  function renderOriginalFiles() {
    return <div>
      {toursData?.file1 && <div
        style={{ color: '#007bff', cursor: 'pointer', overflowWrap: 'anywhere' }}
        onClick={() => handleDownloadFile(toursData?.file1)}
      >
        {toursData?.file1}
      </div>}
      {toursData?.file2 && <div
        style={{ color: '#007bff', cursor: 'pointer', overflowWrap: 'anywhere' }}
        onClick={() => handleDownloadFile(toursData?.file2)}
      >
        {toursData?.file2}
      </div>}
    </div>
  }

  function importedManuallyLabel() {
    const date = dayjs(toursData?.createdAt).format(('DD.MM.YYYY HH:mm'))
    return <>
      {toursData?.ftpImport === 0 ? date : <Icon
        size='large'
        color={'red'}
        name={'close'}
      />}
    </>
  }

  function renderShowTours() {
    return <StyledSegment placeholder>
      <Grid columns={2} stackable>
        <Divider vertical style={{ margin: 0 }} />
        <Grid.Column>
          <Grid columns={2} stackable>
            <Grid.Row>
              <ColumnLabels>{t('Accepted quantity (in l)')}</ColumnLabels>
              <StyledGridColumns>{formatNumberWithoutFractions(toursData?.totalAmount)}</StyledGridColumns>
            </Grid.Row>
            <Grid.Row>
              <ColumnLabels>{t('Collection truck no.')}</ColumnLabels>
              <StyledGridColumns>{toursData?.vehicleNumber}</StyledGridColumns>
            </Grid.Row>
            <Grid.Row>
              <ColumnLabels>{t('Driver no.')}</ColumnLabels>
              <StyledGridColumns>{toursData?.driverNumber}</StyledGridColumns>
            </Grid.Row>
            <Grid.Row>
              <ColumnLabels>{t('Number of samples')}</ColumnLabels>
              <StyledGridColumns>{toursData?.samplesTotal}</StyledGridColumns>
            </Grid.Row>
          </Grid>
        </Grid.Column>
        <Grid.Column>
          <Grid columns={2} stackable style={{ marginLeft: 5 }}>
            <StyledGridRow>
              <ColumnLabels>{t('Tour star / Tour end')}</ColumnLabels>
              <StyledGridColumns>
                {`${dayjs(toursData?.dateStart).format(('DD.MM.YYYY HH:mm'))} / ${dayjs(toursData?.dateEnd).format(('DD.MM.YYYY HH:mm'))}`}
              </StyledGridColumns>
            </StyledGridRow>
            <StyledGridRow>
              <ColumnLabels>{t('Acquisition system')}</ColumnLabels>
              <StyledGridColumns>{toursData?.typeLabel ? toursData.typeLabel : t('Not defined')}</StyledGridColumns>
            </StyledGridRow>
            <Grid.Row>
              <ColumnLabels>{t('Original files')}</ColumnLabels>
              <Grid.Column style={{ paddingLeft: 0 }}>{renderOriginalFiles()}</Grid.Column>
            </Grid.Row>
            <Grid.Row style={{ alignItems: 'end' }}>
              <ColumnLabels>{t('Imported automatically?')}</ColumnLabels>
              <StyledGridColumns>
                {toursData?.ftpImport ? dayjs(toursData?.createdAt).format(('DD.MM.YYYY HH:mm')) : <Icon
                  size='large'
                  color={'red'}
                  name={'close'}
                />}
              </StyledGridColumns>
            </Grid.Row>
            <StyledGridRow>
              <ColumnLabels>{t('Imported manually?')}</ColumnLabels>
              <StyledGridColumns>{importedManuallyLabel()}</StyledGridColumns>
            </StyledGridRow>
          </Grid>
        </Grid.Column>
      </Grid>
    </StyledSegment>
  }

  function renderSearch() {
    return (
      <Input
        style={{ width: 215 }}
        icon={{ name: 'search', circular: true, link: true }}
        placeholder={t('Farmer / Supplier number')}
        value={search}
        onChange={(e, data) => setSearch(data.value)}
      />
    );
  }

  function renderHeader() {
    return <StyledHeader>
      <div className='header'>{t('Milk collection from farmers')}</div>
      <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
        {renderSearch()}
        <Checkbox
          checked={Boolean(dataFiltered.deleted)}
          label={'Gelöschte Milchanlieferungen'}
          onChange={(e, data) => setDataFiltered({
            ...dataFiltered,
            deleted: data.checked ? 1 : 0
          })}
        />
      </div>
    </StyledHeader>
  }

  function handleActionClick(row, type) {
    switch (type) {
      case 'edit':
        return setIdMilkDelivery(row.id)
      case 'delete':
        return setDeleteTour(row)
      case 'reload':
        return setReloadTour(row)
      default:
        return
    }
  }

  function renderTable() {
    return <TableComponent
      actions={removeActions ? [] : actions}
      isFetchingData={isFetchingData}
      animated
      tableColumnHeaders={tableColumnHeaders}
      dataFiltered={dataFiltered}
      handleClickAction={(row, type) => handleActionClick(row, type)}
      handleChangeDataFiltered={(data, tableColumnHeaders) => {
        setTableColumnHeaders(tableColumnHeaders)
        setDataFiltered(data)
      }}
      rows={rows}
      total={filtered}
    />
  }

  function renderSpecificTourData() {
    return <div>
      {renderHeader()}
      {renderTable()}
    </div>
  }

  function renderActions() {
    return <>
      {!removeActions && <El.PageHeaderActionButton
        size='large'
        onClick={() => setIdMilkDelivery('create')}
      >
        {t('Add milk delivery')}
      </El.PageHeaderActionButton>
      }
      {!lightUserLogged && <ExcelExport
        name={t('Export')}
        icon={'file excel'}
        fileName={'exel_file'}
        url={`milk-money/tours/${id}/export`}
      />}
    </>
  }

  function renderDeleteModal() {
    return <DeleteTourModal
      deleteTour={deleteTour}
      handleClose={() => setDeleteTour(null)}
      handleSubmitDelete={() => {
        setDeleteTour(null)
        fetchFilteredData()
      }}
    />
  }

  function renderRestoreTour() {
    return <ReloadTourModal
      reloadTour={reloadTour}
      handleClose={() => setReloadTour(null)}
      handleSubmit={() => {
        setReloadTour(null)
        fetchFilteredData()
      }}
    />
  }

  const removeActions = lightUserLogged || !toursData?.canBeEdited
  const { filtered, rows } = specificTourData ? specificTourData : { filtered: 0, rows: [] };

  return <Layout
    isBusy={isBusy || isBusyData}
    title={toursData && t('Details Tour-Nr. {{number}} from {{date}}',
      {
        number: toursData?.tourNumber,
        date: dayjs(toursData?.createdAt).format('DD.MM.YYYY')
      }
    )}
    segmented
    actions={renderActions()}
    close={RouteTypes.MILK_MONEY_DELIVERY}
  >
    {renderRestoreTour()}
    {renderDeleteModal()}
    {toursData && renderShowTours()}
    {renderSpecificTourData()}
    <AddEditMilkDelivery
      idMilkDelivery={idMilkDelivery}
      isVisible={Boolean(idMilkDelivery)}
      handleHiddenDrawer={(shouldUpdate?) => {
        setIdMilkDelivery(null)
        if (shouldUpdate) {
          fetchFilteredData()
        }
      }}
    />
  </Layout>
}
export default ShowTours